import { HDate, HebrewCalendar, flags, months } from '@hebcal/core';
import { ZmanimData } from '../types';
import { getDayFacts } from './jewishCalendar';
import { planSelichot } from './minyanDay';

/** Parse YYYY-MM-DD the same way as hebcalZmanim (noon UTC avoids day-rollover). */
function hdateFromDateStr(dateStr: string): HDate {
  return new HDate(new Date(`${dateStr}T12:00:00Z`));
}

/**
 * Whether any Selichos can show on this date (spec section 5): Elul from 2 Elul,
 * the first Ashkenaz night, Aseret Yemei Teshuva, minor fasts and BeHaB days;
 * never on Shabbos, Yom Tov or Tisha B'Av. Used for the search page's Selichos chip.
 */
export function isSelichotSeason(dateStr: string): boolean {
  const { levels } = planSelichot(dateStr);
  return levels.elul2.length > 0 || levels.firstNight.length > 0;
}

/**
 * Yom Kippur–specific prayer chips (Neila, Tefilah Zakha): from 2 days before
 * Yom Kippur through Yom Kippur itself (8–10 Tishrei), inclusive.
 */
export function isYomKippurChipWindow(dateStr: string): boolean {
  const hd = hdateFromDateStr(dateStr);
  const month = hd.getMonth();
  const year = hd.getFullYear();

  // Window falls only on 8–10 Tishrei; Elul is never inside it.
  let yomKippur: HDate;
  if (month === months.TISHREI) {
    yomKippur = new HDate(10, months.TISHREI, year);
  } else if (month === months.ELUL) {
    yomKippur = new HDate(10, months.TISHREI, year + 1);
  } else {
    return false;
  }

  const abs = hd.abs();
  return abs >= yomKippur.abs() - 2 && abs <= yomKippur.abs();
}

/**
 * Yizkor is said on Yom Kippur, Simchat Torah, Shvii Shel Pesach and Shavuot,
 * also when that day is Shabbos (decided by Yoel, October 4, 2026).
 */
export function isYizkorEligible(zmanim: ZmanimData): boolean {
  return getDayFacts(zmanim.date).yizkor;
}

/** Hoshana Rabba: 21 Tishrei (the last day of Chol HaMoed Sukkot). */
export function isHoshanaRabba(dateStr: string): boolean {
  const hd = hdateFromDateStr(dateStr);
  return hd.getMonth() === months.TISHREI && hd.getDate() === 21;
}

function isChagOrTishaBav(hd: HDate): { chag: boolean; tishaBav: boolean } {
  const events = HebrewCalendar.getHolidaysOnDate(hd, true) ?? [];
  return {
    chag: events.some((e) => (e.getFlags() & flags.CHAG) !== 0),
    tishaBav: events.some((e) => e.getDesc() === "Tish'a B'Av"),
  };
}

const erevYomTovCache = new Map<string, boolean>();

/**
 * Erev Yom Tov = the day before Yom Tov, Yom Kippur or Tisha B'Av (Israel),
 * when the day itself is not Yom Tov. Decided by the calendar date, so it is the
 * same whichever zmanim provider is used. Unlike Hebcal's own "Erev" flag, this
 * includes Hoshana Rabba and the day before Shvi'i shel Pesach, and leaves out
 * Erev Purim (decided by Yoel, October 2, 2026).
 */
export function isErevYomTovDate(dateStr: string): boolean {
  const cached = erevYomTovCache.get(dateStr);
  if (cached !== undefined) return cached;
  const today = hdateFromDateStr(dateStr);
  const tomorrow = isChagOrTishaBav(today.next());
  const result = !isChagOrTishaBav(today).chag && (tomorrow.chag || tomorrow.tishaBav);
  erevYomTovCache.set(dateStr, result);
  return result;
}
